import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { VideoElement } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/VideoElement';
import { validateAndNormalizeVolume, removeLeadingZeros, VOLUME_MIN, VOLUME_MAX } from './utils';
import PlayerMenuTooltip from '@/App/remotion/components/PlayerMenus/PlayerMenuTooltip';
import useDebounce from '@/hooks/useDebounce';
import { updateElementDetailsHandler } from '@/Pages/Clip/ClipPlayer/ClipAssets/handlers';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import Icon from '@/components/atoms/Icon';

export const VideoElementVolumeControl = ({ selectedElement }: { selectedElement: VideoElement }) => {
  const initialVolume = useMemo(() => {
    const rawValue = selectedElement.details.volume[0]?.value;
    return validateAndNormalizeVolume(rawValue);
  }, [selectedElement.details.volume]);

  const initialMuted = useMemo(() => selectedElement.details.muted ?? false, [selectedElement.details.muted]);
  const [isVolumeMenuOpen, setIsVolumeMenuOpen] = useState(false);
  const [volume, setVolume] = useState(initialVolume);
  const [isMuted, setIsMuted] = useState(initialMuted);

  const [isInputFocused, setIsInputFocused] = useState(false);

  const { clipId, clipData } = useClipsContext();

  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setIsMuted(initialMuted);
  }, [initialMuted]);

  const getVolumeIcon = useMemo(() => {
    if (isMuted) {
      return <Icon name="IconVolumeOff" size={20} />;
    }
    return <Icon name="IconVolume" size={20} />;
  }, [isMuted]);

  const onVolumeChange = useCallback(
    (newVolume: number) => {
      updateElementDetailsHandler(clipData, clipId, selectedElement.id, {
        ...selectedElement.details,
        volume: [{ value: newVolume }],
        muted: newVolume > 0 ? false : true
      });
    },
    [clipData, clipId, selectedElement.details, selectedElement.id]
  );

  const onMuteChange = useCallback(
    (isMuted: boolean) => {
      updateElementDetailsHandler(clipData, clipId, selectedElement.id, {
        ...selectedElement.details,
        muted: isMuted
      });
    },
    [clipData, clipId, selectedElement.details, selectedElement.id]
  );

  const debouncedOnVolumeChange = useDebounce(onVolumeChange, 500);

  const handleVolumeChange = useCallback(
    (newVolume: number | string) => {
      const validatedVolume = validateAndNormalizeVolume(newVolume);
      setVolume(validatedVolume);
      debouncedOnVolumeChange(validatedVolume);
    },
    [debouncedOnVolumeChange]
  );

  const handleVolumeInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      if (inputValue === '') {
        setVolume(0);
        return;
      }

      const cleanValue = inputValue.replace('%', '');

      const normalizedInput = removeLeadingZeros(cleanValue);

      e.target.value = normalizedInput;

      handleVolumeChange(normalizedInput);
    },
    [handleVolumeChange]
  );

  const handleInputFocus = useCallback(() => {
    setIsInputFocused(true);
  }, []);

  const handleInputBlur = useCallback(() => {
    setIsInputFocused(false);
  }, []);

  const handleVolumeButtonClick = useCallback(() => {
    if (isMuted) {
      setVolume(initialVolume);
      setIsMuted(false);
    } else {
      setVolume(0);
      setIsMuted(true);
    }
    onMuteChange(!isMuted);
  }, [isMuted, initialVolume, onMuteChange]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsVolumeMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isVolumeMenuOpen]);

  return (
    <div className={`relative`}>
      <button
        ref={buttonRef}
        onClick={() => setIsVolumeMenuOpen(!isVolumeMenuOpen)}
        className="group/button flex size-10 items-center justify-center rounded-md hover:bg-slate-100"
      >
        {getVolumeIcon}
        <PlayerMenuTooltip content="Volume" />
      </button>

      {isVolumeMenuOpen && (
        <div
          ref={menuRef}
          className="absolute bottom-full left-1/2 mb-1 flex min-w-[300px] -translate-x-1/2 rounded-lg border bg-white px-2 py-1 shadow-lg"
        >
          <div className="flex w-full items-center gap-3">
            <button
              onClick={handleVolumeButtonClick}
              className="group/button flex size-10 items-center justify-center rounded-md hover:bg-slate-100"
            >
              {getVolumeIcon}
              <PlayerMenuTooltip content={isMuted ? 'Unmute' : 'Mute'} />
            </button>
            <input
              type="range"
              min={VOLUME_MIN}
              max={VOLUME_MAX}
              value={volume}
              onChange={e => handleVolumeChange(Number(e.target.value))}
              className="slider h-2 flex-1 cursor-pointer appearance-none rounded-lg"
              style={{
                background: `linear-gradient(to right, #000000 0%, #000000 ${(volume / VOLUME_MAX) * 100}%, #e5e7eb ${(volume / VOLUME_MAX) * 100}%, #e5e7eb 100%)`
              }}
            />

            <div className="relative">
              <input
                type="text"
                value={isInputFocused ? volume : `${volume}%`}
                onChange={handleVolumeInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                className="w-12 rounded-md border border-transparent bg-gray-100 p-1 text-xs focus:border-transparent focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoElementVolumeControl;
